import { Box, Grid, TextField, Typography } from "@mui/material";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import SimpleModal from "common/Form/Form";
import AlertMessages from "common/Alert/AlertMessages";
import Loader from "layout/Loader";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import {
  RackProfileService,
  StoreProfileService,
} from "subModule/src/core/services";
import { ReactComponent as StoreIcon } from "assets/SideBarIcons/store.svg";
import StoreTabs from "../StoreTabs";
import { buildShelfPreviewRows, isBinInventoryMode } from "./rackUtils";
import { getRackCode, getRackTitle } from "subModule/src/services/rack/service";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { useFrozenStoreRackFormBreadcrumbs } from "Helpers/useFrozenStationBreadcrumbs";

interface FormData {
  [key: string]: any;
}

export default function RackModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const storeId = Number(params?.id);
  const rackId = params?.rackId ? Number(params.rackId) : null;
  const isEdit = !!rackId;
  const breadcrumbItems = useFrozenStoreRackFormBreadcrumbs(isEdit);

  const [loading, setLoading] = useState(isEdit);
  const [isBtnEnabled, setIsBtnEnabled] = useState(true);
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      rackTitle: "",
      rackCode: "",
      generateStructure: true,
      numberOfShelf: 3,
      numberOfBin: 4,
      shelfCodePrefix: "S",
      binCodePrefix: "B",
    },
  });

  const generateStructure = !!useWatch({ control, name: "generateStructure" });
  const rackCode = String(useWatch({ control, name: "rackCode" }) || "");
  const numberOfShelf = Number(
    useWatch({ control, name: "numberOfShelf" }) || 0,
  );
  const numberOfBin = Number(useWatch({ control, name: "numberOfBin" }) || 0);
  const shelfCodePrefix = String(
    useWatch({ control, name: "shelfCodePrefix" }) ?? "S",
  );
  const binCodePrefix = String(
    useWatch({ control, name: "binCodePrefix" }) ?? "B",
  );

  useEffect(() => {
    if (isDirty) setIsBtnEnabled(true);
  }, [isDirty]);

  useEffect(() => {
    if (!storeId) return;
    StoreProfileService.getStoreByID(storeId)
      .then((res) => {
        if (res?.success && !isBinInventoryMode(res?.data?.inventoryMode)) {
          navigate(`/store/${storeId}`, { replace: true });
        }
      })
      .catch(() => {});
  }, [storeId, navigate]);

  useEffect(() => {
    if (!isEdit || !storeId || !rackId) return;
    setLoading(true);
    RackProfileService.getRackById(rackId)
      .then((res) => {
        if (!res?.success || !res?.data) {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
          return;
        }
        const rack = res.data;
        reset({
          rackTitle: getRackTitle(rack),
          rackCode: getRackCode(rack),
          generateStructure: false,
          numberOfShelf: 3,
          numberOfBin: 4,
          shelfCodePrefix: "S",
          binCodePrefix: "B",
        });
      })
      .catch((err) => {
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  }, [isEdit, storeId, rackId, enqueueSnackbar, reset]);

  const previewRows = useMemo(() => {
    if (!generateStructure || isEdit) return [];
    return buildShelfPreviewRows(
      rackCode.trim() || "R?",
      shelfCodePrefix,
      binCodePrefix,
      numberOfShelf,
      numberOfBin,
    );
  }, [
    generateStructure,
    isEdit,
    rackCode,
    shelfCodePrefix,
    binCodePrefix,
    numberOfShelf,
    numberOfBin,
  ]);

  const totalBins = previewRows.reduce(
    (sum, row) => sum + row.addresses.length,
    0,
  );

  const onSubmit = (data: FormData) => {
    if (!storeId) return;
    setIsBtnEnabled(false);
    const code = String(data.rackCode || "").trim();
    const title = String(data.rackTitle || "").trim();
    const request = isEdit
      ? RackProfileService.updateRack(rackId!, {
          code,
          title,
          storeId,
        })
      : data.generateStructure
        ? RackProfileService.createRackWithStructure({
            rackCode: code,
            rackTitle: title,
            storeId,
            numberOfShelf: Number(data.numberOfShelf) || 0,
            numberOfBin: Number(data.numberOfBin) || 0,
            shelfCodePrefix: data.shelfCodePrefix,
            binCodePrefix: data.binCodePrefix,
          })
        : RackProfileService.createRack({
            code,
            title,
            storeId,
          });

    request
      .then((res: any) => {
        if (res?.success) {
          enqueueSnackbar(
            <AlertMessages
              text={t(isEdit ? "updatedSuccessfully" : "createdSuccessfully")}
            />,
            { variant: "success" },
          );
          navigate(`/store/${storeId}/racks`);
        } else {
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        setIsBtnEnabled(true);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };

  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(`/store/${storeId}/racks`)}
        onSubmit={handleSubmit(onSubmit)}
        heading={isEdit ? t("editRack") : t("createRack")}
        icon={<StoreIcon height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={isBtnEnabled}
        showCreateBtn={false}
        saveBtnText={isEdit ? "update" : "submit"}
      />
      <div className="flex">
        <StoreTabs params={params} selectedIndex={1} showRacks />
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={isEdit ? t("editRack") : t("createRack")}
            isButtonVisable={true}
            isVisableBtn={isBtnEnabled}
            btnclosetext="Back"
            buttonText={isEdit ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-baseline mb-2"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("rackTitle", {
                      validate: {
                        required: (value) =>
                          String(value || "").trim() !== "" ||
                          t("rackNameRequired"),
                      },
                    })}
                    label={t("rackName") + " *"}
                    placeholder={t("rackName")}
                    title={t("rackName")}
                    fullWidth
                    error={!!errors.rackTitle}
                    helperText={errors.rackTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("rackCode", {
                      validate: {
                        required: (value) =>
                          String(value || "").trim() !== "" ||
                          t("rackCodeRequired"),
                      },
                    })}
                    label={t("rackCode") + " *"}
                    placeholder={t("rackCode")}
                    title={t("rackCode")}
                    fullWidth
                    error={!!errors.rackCode}
                    helperText={errors.rackCode?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {!isEdit ? (
                  <Grid item xs={12} md={12}>
                    <Controller
                      name="generateStructure"
                      control={control}
                      defaultValue={true}
                      render={({ field }) => (
                        <div>
                          <CommonCheckbox
                            {...field}
                            label={t("generateShelvesBinsNow")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => field.onChange(event.target.checked)}
                            checked={!!field.value}
                            color={themeColors?.primary}
                          />
                          <p className="text-start font-Saira text-[11px] text-[#6B7A8D]  ml-[2px]">
                            {t("generateShelvesBinsHelper")}
                          </p>
                        </div>
                      )}
                    />
                  </Grid>
                ) : null}

                {!isEdit && generateStructure ? (
                  <>
                    <Grid item xs={12}>
                      <Typography className="text-start  font-Saira font-semibold text-[13px] text-txt-color">
                        {t("structure")}
                      </Typography>
                      <hr className="my-1 border-t-2 border-gray-300" />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="numberOfShelf"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label={t("numberOfShelves")}
                            placeholder={t("numberOfShelves")}
                            title={t("numberOfShelves")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0 }}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                              )
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="numberOfBin"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label={t("binsPerShelf")}
                            placeholder={t("binsPerShelf")}
                            title={t("binsPerShelf")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0 }}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                              )
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="shelfCodePrefix"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={t("shelfCodePrefix")}
                            placeholder="S"
                            title={t("shelfCodePrefix")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="binCodePrefix"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={t("binCodePrefix")}
                            placeholder="B"
                            title={t("binCodePrefix")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className="font-Saira font-semibold text-[13px] text-txt-color mb-2">
                        {t("preview")}
                      </Typography>
                      <Box className="flex justify-between items-center mb-2">
                        <Typography className="font-Saira font-semibold text-[12px]">
                          {t("addressesToBeCreated")}
                        </Typography>
                        <Typography className="font-Saira text-[11px] text-[#6B7A8D]">
                          {totalBins} {t("binsAcross")} {previewRows.length}{" "}
                          {t("shelves")}
                        </Typography>
                      </Box>
                      <Box className="flex flex-col gap-2 max-h-[220px] overflow-auto">
                        {previewRows.map((row) => (
                          <Box
                            key={`shelf-${row.shelfIndex}`}
                            className="flex flex-wrap gap-2"
                          >
                            {row.addresses.map((addr, binIndex) => (
                              <Box
                                key={`${row.shelfIndex}-${binIndex}-${addr}`}
                                className="font-Saira text-[11px] border rounded px-2 py-1 text-center min-w-[88px]"
                                sx={{
                                  color: themeColors?.primary,
                                  borderColor: themeColors?.primary,
                                  backgroundColor: `${themeColors?.secondary}26`,
                                }}
                              >
                                {addr}
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </>
                ) : null}
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
